import { ArrowCircleDown, ArrowCircleUp, Package, IconProps } from 'phosphor-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';
import { IFiscalContabilItem } from '../../../../hooks/useFiscalContabil';

export const TIPO_FISCAL = {
  1: { label: 'Entrada', color: 'green', icon: ArrowCircleDown },
  2: { label: 'Saida', color: 'orange', icon: ArrowCircleUp },
  3: { label: 'Servico', color: 'purple', icon: Package },
} as const;

export type TipoFiscalKey = keyof typeof TIPO_FISCAL;

export interface TipoFiscalConfig {
  label: string;
  color: string;
  icon: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
}

export const COLORS = {
  entrada: { bg: 'green.50', text: 'green.700', accent: '#38A169', border: 'green.400' },
  saida: { bg: 'orange.50', text: 'orange.700', accent: '#DD6B20', border: 'orange.400' },
  servico: { bg: 'purple.50', text: 'purple.700', accent: '#805AD5', border: 'purple.400' },
  divergencia: { bg: 'red.50', text: 'red.700', accent: '#E53E3E', border: 'red.400' },
} as const;

// Cores hex por número de tipo — fonte única de verdade reutilizada nos charts.
export const TIPO_HEX_COLORS: Record<number, string> = {
  1: COLORS.entrada.accent,
  2: COLORS.saida.accent,
  3: COLORS.servico.accent,
};

export const TIPO_HEX_COLORS_LIGHT: Record<number, string> = {
  1: '#9AE6B4',
  2: '#FBD38D',
  3: '#D6BCFA',
};

export const PIE_COLORS = [TIPO_HEX_COLORS[1], TIPO_HEX_COLORS[2], TIPO_HEX_COLORS[3]];

export const tipoOptions = [
  { label: 'Todos', value: '' },
  { label: 'Entrada', value: '1' },
  { label: 'Saida', value: '2' },
  { label: 'Servico', value: '3' },
];

export interface IDivergencia {
  quantidade: number;
  valorContabilTotal: number;
  valorTotal: number;
  diferenca: number;
  totalEmpresas: number;
}

export interface IResumoCalculado {
  totalRegistros: number;
  valorContabilTotal: number;
  totalEntradas: number;
  totalSaidas: number;
  totalServicos: number;
  valorEntradas: number;
  valorSaidas: number;
  valorServicos: number;
  divergenciasEntradas: number;
  divergenciasSaidas: number;
  divergenciasServicos: number;
}

export interface IAcumuladorGrupo {
  acumulador: number | null | undefined;
  acumuladorNome: string;
  quantidade: number;
  valorTotal: number;
}

export const getTipoConfig = (tipo: number): TipoFiscalConfig => {
  return TIPO_FISCAL[tipo as TipoFiscalKey] || TIPO_FISCAL[1];
};

export const getTipoColor = (tipo: number): string => {
  return TIPO_FISCAL[tipo as TipoFiscalKey]?.color || 'gray';
};

export const DIVERGENCIA_TOLERANCIA = 0.01;

// Coerção numérica defensiva — diferente de `Number(x) || 0`, que silencia NaN.
// Aceita strings/numbers/null/undefined; retorna `defaultVal` se o valor não for finito.
export const safeNumber = (val: unknown, defaultVal = 0): number => {
  if (val === null || val === undefined || val === '') return defaultVal;
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n) ? n : defaultVal;
};

export const calcDiferencaAbsoluta = (item: IFiscalContabilItem): number => {
  return Math.abs(safeNumber(item.valor_contabil) - safeNumber(item.valor));
};

export const isItemDivergente = (item: IFiscalContabilItem): boolean => {
  return calcDiferencaAbsoluta(item) > DIVERGENCIA_TOLERANCIA;
};
